import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Brain, BarChart3, Building2, Zap, Coins, Clock, Activity } from 'lucide-react';

export default function AdminAIDashboard() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: d } = await api.get(`/admin/ai/dashboard?period=${period}`);
        setData(d);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    };
    load();
  }, [period]);

  if (loading) return <PantallaCargando />;
  if (!data) return <p className="text-sm text-[#94A3B8] text-center py-10">Sin datos</p>;

  const { kpis, top_tenants } = data;

  return (
    <div className="space-y-6" data-testid="admin-ai-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2">
            <Brain className="w-6 h-6" /> IA — Panel Administrativo
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Métricas globales de uso de IA</p>
        </div>
        <div className="flex gap-2">
          {['daily', 'monthly', 'yearly'].map(p => (
            <button key={p} onClick={() => { setPeriod(p); setLoading(true); }}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${period === p ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-[#64748B] border-[#E2E8F0]'}`}
              data-testid={`admin-period-${p}`}>
              {p === 'daily' ? 'Hoy' : p === 'monthly' ? 'Mes' : 'Año'}
            </button>
          ))}
        </div>
      </div>

      {/* Global KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-ai-kpis">
        <KPI icon={Activity} label="Requests" value={kpis.requests} />
        <KPI icon={Zap} label="Tokens" value={kpis.tokens?.toLocaleString()} />
        <KPI icon={Coins} label="Costo (MXN)" value={`$${kpis.cost?.toFixed(4)}`} />
        <KPI icon={BarChart3} label="Éxito" value={`${kpis.success_rate}%`} />
        <KPI icon={Clock} label="Latencia avg" value={`${kpis.avg_latency_ms}ms`} />
      </div>

      {/* Top Tenants */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="admin-top-tenants">
        <h3 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
          <Building2 className="w-4 h-4" /> Top Tenants por Costo
        </h3>
        {top_tenants.length === 0 ? (
          <p className="text-xs text-[#94A3B8] text-center py-4">Sin tenants con uso de IA</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-[#64748B] border-b border-[#E2E8F0]">
                <th className="text-left py-2">Tenant</th>
                <th className="text-right py-2">Requests</th>
                <th className="text-right py-2">Tokens</th>
                <th className="text-right py-2">Costo (MXN)</th>
              </tr></thead>
              <tbody>
                {top_tenants.map(t => (
                  <tr key={t.tenant_id} className="border-b border-[#F1F5F9]">
                    <td className="py-2 text-[#0F172A] font-medium">{t.tenant_name}</td>
                    <td className="text-right py-2">{t.requests}</td>
                    <td className="text-right py-2">{t.tokens?.toLocaleString()}</td>
                    <td className="text-right py-2">${t.cost?.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-[#94A3B8]" />
        <span className="text-[10px] font-medium text-[#64748B] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-lg font-bold text-[#0F172A]">{value}</p>
    </div>
  );
}
